/*
 * Purpose: Mission document library — add/list any doc type for any mission
 * Last modified: 2026-06-28 | Agent
 * Completeness: 95
 */
import { useEffect, useState } from 'react';
import { FileStack, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useMissionKnowledgeStore } from '@/stores/missionKnowledgeStore';
import { cn } from '@/lib/utils';

interface MissionDocumentsLibraryProps {
  knowledgeGaps?: string[];
  className?: string;
  formOpen?: boolean;
  onFormOpenChange?: (open: boolean) => void;
  initialLinkedGap?: string;
  initialTitle?: string;
}

export function MissionDocumentsLibrary({
  knowledgeGaps = [],
  className,
  formOpen,
  onFormOpenChange,
  initialLinkedGap,
  initialTitle,
}: MissionDocumentsLibraryProps) {
  const docs = useMissionKnowledgeStore((s) => s.docs);
  const addDoc = useMissionKnowledgeStore((s) => s.addDoc);
  const removeDoc = useMissionKnowledgeStore((s) => s.removeDoc);

  const [internalOpen, setInternalOpen] = useState(docs.length === 0);
  const expanded = formOpen ?? internalOpen;
  const setExpanded = onFormOpenChange ?? setInternalOpen;

  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [body, setBody] = useState('');
  const [linkedGap, setLinkedGap] = useState(knowledgeGaps[0] ?? '');

  useEffect(() => {
    if (initialLinkedGap) setLinkedGap(initialLinkedGap);
    if (initialTitle) setTitle(initialTitle);
  }, [initialLinkedGap, initialTitle]);

  function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return;
    addDoc({
      title,
      body,
      category: category.trim() || undefined,
      gapRefs: linkedGap ? [linkedGap] : [],
    });
    setTitle('');
    setCategory('');
    setBody('');
    if (docs.length > 0) setExpanded(false);
  }

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex items-start gap-2">
        <FileStack className="h-4 w-4 text-primary shrink-0 mt-0.5" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-foreground">Mission documents</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Documents for this Mission only — any type, any content. Not visible to other Missions.
          </p>
        </div>
        {docs.length > 0 && !expanded && (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="shrink-0 h-7 text-[11px]"
            onClick={() => setExpanded(true)}
          >
            <Plus className="h-3 w-3 mr-1" />
            Add document
          </Button>
        )}
      </div>

      {docs.length > 0 && (
        <ul className="space-y-2">
          {docs.map((doc) => (
            <li
              key={doc.id}
              className="rounded-md border border-border/50 bg-card px-3 py-2 text-xs space-y-1"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium text-foreground flex flex-wrap items-center gap-1.5">
                    {doc.title}
                    {doc.category && (
                      <Badge variant="outline" className="text-[10px] font-normal">
                        {doc.category}
                      </Badge>
                    )}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0"
                  aria-label={`Remove ${doc.title}`}
                  onClick={() => removeDoc(doc.id)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
              <p className="text-muted-foreground line-clamp-4 whitespace-pre-wrap">{doc.body}</p>
            </li>
          ))}
        </ul>
      )}

      {expanded && (
        <form onSubmit={handleAdd} className="space-y-3 rounded-md border border-dashed border-border/70 p-3">
          {knowledgeGaps.length > 0 && (
            <div>
              <Label htmlFor="mission-doc-gap" className="text-xs">
                Relates to gap (optional)
              </Label>
              <select
                id="mission-doc-gap"
                className="mt-1 w-full h-9 rounded-md border border-input bg-background px-2 text-xs"
                value={linkedGap}
                onChange={(e) => setLinkedGap(e.target.value)}
              >
                <option value="">General mission context</option>
                {knowledgeGaps.map((g) => (
                  <option key={g} value={g}>
                    {g.length > 72 ? `${g.slice(0, 69)}…` : g}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="mission-doc-title" className="text-xs">
                Title
              </Label>
              <Input
                id="mission-doc-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Name this document"
                className="mt-1 h-9 text-sm"
              />
            </div>
            <div>
              <Label htmlFor="mission-doc-category" className="text-xs">
                Category (optional)
              </Label>
              <Input
                id="mission-doc-category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="Any label you want"
                className="mt-1 h-9 text-sm"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="mission-doc-body" className="text-xs">
              Content
            </Label>
            <Textarea
              id="mission-doc-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Paste any content — plain text, markdown, exports…"
              rows={5}
              className="mt-1 text-sm font-mono text-[13px]"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="submit" size="sm" disabled={!title.trim() || !body.trim()}>
              Save document
            </Button>
            {docs.length > 0 && (
              <Button type="button" size="sm" variant="ghost" onClick={() => setExpanded(false)}>
                Cancel
              </Button>
            )}
          </div>
        </form>
      )}
    </div>
  );
}
